// The Flows registry's filter bar — a name search plus app, task-type and team facets,
// each a multi-select drop-down (`core/filter-select.ts`) so the bar is one line however
// many apps and teams the registry holds.
//
// Filtering is client-side off each row's stamped `data-*` attributes, like the farm list's
// filter, so the list is filtered without refetching or holding onto the records.
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { filterSelect, pickFilterSelect, wireFilterSelects } from '../core/filter-select';
import { TASK_TYPES } from './flows-shared';
export function createFlowFilterState() {
    return { name: '', apps: new Set(), tasks: new Set(), teams: new Set() };
}
/** Stamp what {@link applyFlowFilter} reads onto one rendered flow row. */
export function stampFlowFilterData(el, f) {
    const ds = el.dataset;
    ds['name'] = f.name.toLowerCase();
    ds['app'] = f.app;
    ds['task'] = f.task_type;
    // A global flow belongs to every team, so it answers to any team chip.
    ds['teams'] = f.is_global ? '*' : f.team_ids.join(',');
}
function teamsMatch(selected, stamped) {
    if (selected.size === 0)
        return true;
    if (stamped === '*')
        return true;
    return stamped.split(',').some((id) => selected.has(Number.parseInt(id, 10)));
}
function rowMatches(state, ds) {
    if (state.name && !(ds['name'] ?? '').includes(state.name))
        return false;
    if (state.apps.size && !state.apps.has(ds['app'] ?? ''))
        return false;
    if (state.tasks.size && !state.tasks.has((ds['task'] ?? '')))
        return false;
    return teamsMatch(state.teams, ds['teams'] ?? '');
}
/** Show/hide the already-rendered rows per the current selection. Returns what is left. */
export function applyFlowFilter(list, state, rowSelector = '.pf-row') {
    let shown = 0;
    list.querySelectorAll(rowSelector).forEach((row) => {
        const ok = rowMatches(state, row.dataset);
        row.style.display = ok ? '' : 'none';
        if (ok)
            shown += 1;
    });
    return shown;
}
/**
 * Render the bar into `el` and wire it. The app options come from the flows themselves —
 * an app nobody has written a flow for is an option that can only ever empty the list —
 * and the teams from the registry's own scopes, so nothing is fetched here.
 */
export function buildFlowFilter(el, state, flows, teams, onChange) {
    const apps = [...new Set(flows.map((f) => f.app))].sort((a, b) => a.localeCompare(b));
    // Drop selections whose chip is gone (an app or a team that no longer has flows),
    // otherwise the list silently filters against something nobody can unselect.
    for (const app of [...state.apps])
        if (!apps.includes(app))
            state.apps.delete(app);
    for (const id of [...state.teams])
        if (!teams.some((tm) => tm.id === id))
            state.teams.delete(id);
    el.innerHTML = `
        <input type="text" class="farm-filter__search" placeholder="${esc(t('pf.search_flows'))}" value="${esc(state.name)}" />
        ${filterSelect(t('pf.task_type'), 'task', TASK_TYPES.map((tt) => ({ key: tt, label: t(`pf.task_${tt}`), on: state.tasks.has(tt) })))}
        ${filterSelect(t('pf.app'), 'app', apps.map((a) => ({ key: a, label: a, on: state.apps.has(a) })))}
        ${filterSelect(t('nav.teams'), 'team', teams.map((tm) => ({ key: String(tm.id), label: tm.name, on: state.teams.has(tm.id) })))}
    `;
    const search = el.querySelector('.farm-filter__search');
    search.addEventListener('input', () => { state.name = search.value.trim().toLowerCase(); onChange(); });
    // The bar is rebuilt on every list reload into the SAME element, so the delegated
    // handlers are wired once — a second one would toggle each selection twice.
    if (el.dataset['wired'])
        return;
    el.dataset['wired'] = '1';
    wireFilterSelects(el);
    el.addEventListener('click', (e) => {
        const opt = e.target.closest('.fsel__opt');
        if (!opt || !el.contains(opt))
            return;
        const key = opt.dataset['key'] ?? '';
        const fsel = opt.closest('.fsel');
        const on = pickFilterSelect(opt);
        const put = (set, value) => { if (on)
            set.add(value);
        else
            set.delete(value); };
        if (fsel.dataset['role'] === 'task')
            put(state.tasks, key);
        else if (fsel.dataset['role'] === 'app')
            put(state.apps, key);
        else if (fsel.dataset['role'] === 'team')
            put(state.teams, Number.parseInt(key, 10));
        onChange();
    });
}
